import { createAsyncThunk } from "@reduxjs/toolkit";
import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { JSON_CONTENT_TYPE } from "constants/app";
import { showErrorModal, validateErrorResponse } from "slices/app/actions/error";
import {
  addToJobQueue,
  pageLoading,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import { fetchOpportunityByOpportunityId } from "slices/opportunity/summary/actions";
import { selectAdditionalFilters } from "slices/opportunity/tracked/selectors";
import { store } from "slices/store";

import {
  archiveById,
  INITIAL_SORTING,
  removeById,
  setCurrentCount,
  setData,
  setGateMode,
  setIsCountInitialized,
  setIsInitialized,
  setIsLoading,
  setKanbanColumns,
  setTotalCount,
} from "./trackedSlice";

const HEADERS = {
  headers: {
    "Content-Type": JSON_CONTENT_TYPE,
  },
};

export const fetchTrackedOppsAsync = createAsyncThunk(
  "opportunity/tracked",
  async (payload = {}) => {
    const url = "/nextgen/entities/allTracked";
    try {
      const rsp = await localhost.post(url, payload, HEADERS);

      return rsp?.data || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  }
);

export const fetchTrackedOpportunities =
  (sorting = INITIAL_SORTING) =>
  async (dispatch) => {
    const state = store.getState();
    const additionalFilters = selectAdditionalFilters(state);

    const payload = {
      sortOrder: sorting.direction,
      sortColumn: sorting.field,
      ...additionalFilters,
    };

    dispatch(setIsInitialized(false));
    dispatch(setIsLoading(true));
    dispatch(fetchTrackedOppsCount());

    const data = await dispatch(fetchTrackedOppsAsync(payload));
    dispatch(setData(data?.payload));

    dispatch(setIsInitialized(true));
    dispatch(setIsLoading(false));
  };

export const fetchBestFitOpportunities = () => async (dispatch) => {
  const url = "/nextgen/entities/allBestFit";

  dispatch(setIsInitialized(false));
  dispatch(setIsLoading(true));
  dispatch(fetchTrackedOppsCount());
  try {
    const rsp = await localhost.get(url, HEADERS);
    const data = rsp?.data || [];

    dispatch(setData(data));
  } catch (err) {
    console.error(err);
  }

  dispatch(setIsInitialized(true));
  dispatch(setIsLoading(false));
};

export const fetchTrackedOppsCount = () => async (dispatch) => {
  const url = "/nextgen/opportunity/count";
  const state = store.getState();
  const additionalFilters = selectAdditionalFilters(state);

  const payload = {
    ...additionalFilters,
  };

  // Cleared for the duration of the request: the header CTA and the empty banner both read
  // this flag, and a stale count left standing as initialised shows the "no tracked
  // opportunities" banner over a list that does have rows.
  dispatch(setIsCountInitialized(false));
  try {
    const rsp = await localhost.post(url, payload, HEADERS);
    const data = rsp?.data || 0;

    dispatch(setCurrentCount(data));
    dispatch(setTotalCount(data));
    dispatch(setIsCountInitialized(true));
  } catch (err) {
    console.error(err);
  }
};

export const fetchArchivedOpportunities = () => async (dispatch) => {
  dispatch(addToJobQueue("fetchArchivedOpportunities"));
  dispatch(setData([]));
  try {
    const rsp = await localhost.get("/nextgen/entities/allArchived", HEADERS);
    const data = rsp?.data || [];
    dispatch(setData(data));
  } catch (err) {
    dispatch(showErrorModal(err));
  } finally {
    dispatch(removeFromJobQueue("fetchArchivedOpportunities"));
  }
};

export const unarchiveOpportunity = (id) => async (dispatch) => {
  dispatch(addToJobQueue("unarchiveOpportunity"));
  try {
    await localhost.post(`/nextgen/opportunity/${id}/unarchive`, {}, HEADERS);
    dispatch(removeById(id));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: "Opportunity unarchived successfully",
      })
    );
  } catch (err) {
    dispatch(showErrorModal(err));
  } finally {
    dispatch(removeFromJobQueue("unarchiveOpportunity"));
  }
};

export const exportTrackedOpportunitiesToCsv = (searchBy, sorting) => async (dispatch) => {
  dispatch(addToJobQueue("exportTrackedOpportunitiesToCsv"));
  try {
    const params = {};
    if (searchBy) params.searchBy = searchBy;
    if (sorting?.field) params.sortColumn = sorting.field;
    if (sorting?.direction) params.sortOrder = sorting.direction;
    const rsp = await localhost.get("nextgen/entities/tracked/export/csv", { params });
    return new Blob([rsp?.data]);
  } catch (err) {
    dispatch(showErrorModal(err));
    return null;
  } finally {
    dispatch(removeFromJobQueue("exportTrackedOpportunitiesToCsv"));
  }
};

export const archiveOpportunity = (id, { archiveStatusId, archiveNote } = {}) => async (dispatch) => {
  dispatch(addToJobQueue("archiveOpportunity"));
  try {
    await localhost.post(`/nextgen/opportunity/${id}/archive`, { archiveStatusId, archiveNote }, HEADERS);
    dispatch(archiveById(id));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: "Opportunity archived successfully",
      })
    );
  } catch (err) {
    dispatch(validateErrorResponse(err));
    throw err;
  } finally {
    dispatch(removeFromJobQueue("archiveOpportunity"));
  }
};

export const editOpportunityArchiveReason = ({ id, archiveStatusId, archiveNote }) => async (dispatch) => {
  dispatch(addToJobQueue("editOpportunityArchiveReason"));
  try {
    await localhost.put(`/nextgen/opportunity/${id}/archive-reason`, { archiveStatusId, archiveNote }, HEADERS);
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: "Opportunity archive reason updated successfully",
      })
    );
    await dispatch(fetchOpportunityByOpportunityId(id));
  } catch (err) {
    dispatch(validateErrorResponse(err));
    throw err;
  } finally {
    dispatch(removeFromJobQueue("editOpportunityArchiveReason"));
  }
};

export const fetchOpportunityKanban = () => async (dispatch) => {
  try {
    const rsp = await localhost.get("/nextgen/opportunity-kanban");
    dispatch(setKanbanColumns(rsp?.data?.columns || []));
    dispatch(setGateMode(rsp?.data?.gateMode));
  } catch (err) {
    dispatch(showErrorModal(err));
  } finally {
    dispatch(setIsInitialized(true));
  }
};

export const updateOpportunityPosition =
  (prevColumnId, nextColumnId, opportunityId, targetIndex) =>
  async (dispatch) => {
    dispatch(pageLoading(true));
    const params = {
      columnId: nextColumnId,
      position: targetIndex,
    };
    try {
      const rsp = await localhost.put(
        `/nextgen/opportunity-kanban/${opportunityId}/position`,
        params
      );
      dispatch(setKanbanColumns(rsp?.data?.columns || []));
      dispatch(setGateMode(rsp?.data?.gateMode));
    } catch (err) {
      dispatch(showErrorModal(err));
    } finally {
      dispatch(pageLoading(false));
    }
  };

// Skips pageLoading deliberately: the gate move modal stays open (its own
// submitting state disables Confirm) rather than showing the full-page mask.
export const submitOpportunityMove = createAsyncThunk(
  "tracked/submitOpportunityMove",
  async ({ entityId, columnId, position, gate }, { dispatch, rejectWithValue }) => {
    try {
      const rsp = await localhost.put(`/nextgen/opportunity-kanban/${entityId}/position`, {
        columnId,
        position,
        gate,
      });
      dispatch(setKanbanColumns(rsp?.data?.columns || []));
      dispatch(setGateMode(rsp?.data?.gateMode));
      return rsp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Reuses submitOpportunityMove's PUT/response contract, but also refreshes the
// list rows: a list-triggered move must update both the kanban sub-slice
// (kanbanColumns/gateMode, read by the dropdown and computeGateInterception)
// and the list rows (so the moved row shows its new stage).
export const moveOpportunityStageFromList = createAsyncThunk(
  "tracked/moveOpportunityStageFromList",
  async ({ entityId, columnId, position = null, gate = null }, { dispatch, rejectWithValue }) => {
    try {
      const rsp = await localhost.put(`/nextgen/opportunity-kanban/${entityId}/position`, {
        columnId,
        position,
        gate,
      });
      dispatch(setKanbanColumns(rsp?.data?.columns || []));
      dispatch(setGateMode(rsp?.data?.gateMode));
      dispatch(fetchTrackedOpportunities());
      return rsp.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
